import connecticutCalculatorData from '../data/connecticutCalculatorData.json';
import washingtonCalculatorData from '../data/washingtonCalculatorData.json';
import arkansasCalculatorData from '../data/arkansasCalculatorData.json';
import {
  DISTRICT_INDICATORS,
  REGION_INDICATORS,
  WASHINGTON_DISTRICT_INDICATORS,
  ARKANSAS_DISTRICT_INDICATORS,
  ARKANSAS_COUNTY_INDICATORS,
  withEnrollmentYear,
} from '../indicatorConfig';

const COMMON_LEVEL_FIELDS = {
  enrollmentField: 'k12Enrollment2025_2026',
  nameField: 'name',
};

/**
 * Per-location Opportunity Calculator configuration.
 *
 * Every location gets a `data` payload (the calculatorData.json shape:
 * `{ meta, districts, regions? }`), a `levels` map (only locations with a
 * planning-region tier, i.e. Connecticut today, have a `region` entry), and
 * `copy` for the strings/labels that vary by geography and partner. Adding a
 * new City Explorer location's calculator should only require an entry here
 * plus a data file and (if the indicators differ) an indicatorConfig.js
 * export — OpportunityCalculator.js, RankingTable.js, EntityScoreDetail.js,
 * and insights.js should not need location-specific branches.
 */
export const CALCULATOR_DATASETS = {
  connecticut: {
    id: 'connecticut',
    data: connecticutCalculatorData,
    levels: {
      district: {
        id: 'district',
        label: 'District',
        entityNoun: 'district',
        entityNounPlural: 'districts',
        indicators: DISTRICT_INDICATORS,
        entityKey: 'districts',
        metaLabel: 'Planning Region',
        metaField: 'region',
        hasAlliance: true,
        ...COMMON_LEVEL_FIELDS,
      },
      region: {
        id: 'region',
        label: 'Region',
        entityNoun: 'region',
        entityNounPlural: 'regions',
        indicators: REGION_INDICATORS,
        entityKey: 'regions',
        metaLabel: 'Districts',
        metaField: 'districtCount',
        hasAlliance: false,
        ...COMMON_LEVEL_FIELDS,
      },
    },
    copy: {
      regionName: 'Connecticut',
      csvPrefix: 'connecticut',
    },
  },
  washington: {
    id: 'washington',
    data: washingtonCalculatorData,
    levels: {
      district: {
        id: 'district',
        label: 'District',
        entityNoun: 'district',
        entityNounPlural: 'districts',
        indicators: withEnrollmentYear(WASHINGTON_DISTRICT_INDICATORS, washingtonCalculatorData.meta?.enrollmentYear),
        enrollmentYear: washingtonCalculatorData.meta?.enrollmentYear,
        entityKey: 'districts',
        metaLabel: null,
        metaField: null,
        hasAlliance: false,
        ...COMMON_LEVEL_FIELDS,
      },
    },
    copy: {
      regionName: 'Washington',
      csvPrefix: 'washington',
    },
  },
  // Same statewide indicator set as Washington, with ATLAS % met standard
  // instead of SBAC and the A/B letter-grade share added to school quality.
  // Arkansas also offers a county tier (counties aggregate several
  // districts each) with a District/County toggle, the same mechanism as
  // Connecticut's District/Region toggle.
  arkansas: {
    id: 'arkansas',
    data: arkansasCalculatorData,
    levels: {
      district: {
        id: 'district',
        label: 'District',
        entityNoun: 'district',
        entityNounPlural: 'districts',
        indicators: withEnrollmentYear(ARKANSAS_DISTRICT_INDICATORS, arkansasCalculatorData.meta?.enrollmentYear),
        enrollmentYear: arkansasCalculatorData.meta?.enrollmentYear,
        entityKey: 'districts',
        metaLabel: null,
        metaField: null,
        hasAlliance: false,
        ...COMMON_LEVEL_FIELDS,
      },
      county: {
        id: 'county',
        label: 'County',
        entityNoun: 'county',
        entityNounPlural: 'counties',
        indicators: withEnrollmentYear(ARKANSAS_COUNTY_INDICATORS, arkansasCalculatorData.meta?.enrollmentYear),
        enrollmentYear: arkansasCalculatorData.meta?.enrollmentYear,
        entityKey: 'counties',
        metaLabel: null,
        metaField: null,
        hasAlliance: false,
        ...COMMON_LEVEL_FIELDS,
      },
    },
    copy: {
      regionName: 'Arkansas',
      csvPrefix: 'arkansas',
    },
  },
};

export const DEFAULT_CALCULATOR_DATASET_ID = 'connecticut';

export function getCalculatorDataset(locationId) {
  return CALCULATOR_DATASETS[locationId] || CALCULATOR_DATASETS[DEFAULT_CALCULATOR_DATASET_ID];
}

export function levelIdsFor(dataset) {
  return Object.keys(dataset.levels);
}

export function hasMultipleLevels(dataset) {
  return levelIdsFor(dataset).length > 1;
}

export function entitiesForLevel(dataset, levelId) {
  const levelConfig = dataset.levels[levelId];
  if (!levelConfig) return [];
  return dataset.data[levelConfig.entityKey] || [];
}
